import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import {
  LinkPreAppointmentDocumentPayload,
  PreAppointmentDocumentAttachment,
} from "../types/servicePreAppointment";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";

interface ServicePreAppointmentDocumentsContextType {
  isLoading: boolean;
  error: string | null;
  listPreAppointmentDocuments: (
    salonServiceId: string,
  ) => Promise<PreAppointmentDocumentAttachment[]>;
  linkPreAppointmentDocument: (
    salonServiceId: string,
    payload: LinkPreAppointmentDocumentPayload,
  ) => Promise<PreAppointmentDocumentAttachment>;
  unlinkPreAppointmentDocument: (
    salonServiceId: string,
    attachmentUuid: string,
  ) => Promise<void>;
}

const ServicePreAppointmentDocumentsContext =
  createContext<ServicePreAppointmentDocumentsContextType | null>(null);

function extractMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "An unexpected error occurred";
}

export function ServicePreAppointmentDocumentsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businessInfo } = useSalon() as {
    businessInfo: { id?: string } | null;
  };
  const { fetchWithAuth } = useApp();

  const requireSalon = useCallback((): string => {
    const id = businessInfo?.id;
    if (!id) throw new Error("No salon ID available");
    return id;
  }, [businessInfo?.id]);

  const listPreAppointmentDocuments = useCallback(
    async (
      salonServiceId: string,
    ): Promise<PreAppointmentDocumentAttachment[]> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: { documents?: PreAppointmentDocumentAttachment[] } =
          await fetchWithAuth(
            getApiUrl(
              API_CONFIG.ENDPOINTS.SERVICE_PRE_APPOINTMENT_DOCUMENTS,
              "",
              {
                salonId,
                salonServiceId,
              },
            ),
          );
        return response?.documents ?? [];
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const linkPreAppointmentDocument = useCallback(
    async (
      salonServiceId: string,
      payload: LinkPreAppointmentDocumentPayload,
    ): Promise<PreAppointmentDocumentAttachment> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: PreAppointmentDocumentAttachment = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_PRE_APPOINTMENT_DOCUMENTS,
            "",
            {
              salonId,
              salonServiceId,
            },
          ),
          { method: "POST", body: JSON.stringify(payload) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const unlinkPreAppointmentDocument = useCallback(
    async (salonServiceId: string, attachmentUuid: string): Promise<void> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_PRE_APPOINTMENT_DOCUMENT_BY_ID,
            "",
            {
              salonId,
              salonServiceId,
              attachmentUuid,
            },
          ),
          { method: "DELETE" },
        );
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  // [LOOP] churn probe
  useValueChurnProbe("ServicePreAppointmentDocuments", { isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: ServicePreAppointmentDocumentsContextType = useMemo(
    () => ({
      isLoading,
      error,
      listPreAppointmentDocuments,
      linkPreAppointmentDocument,
      unlinkPreAppointmentDocument,
    }),
    [
      isLoading,
      error,
      listPreAppointmentDocuments,
      linkPreAppointmentDocument,
      unlinkPreAppointmentDocument,
    ],
  );

  return (
    <ServicePreAppointmentDocumentsContext.Provider value={value}>
      {children}
    </ServicePreAppointmentDocumentsContext.Provider>
  );
}

export function useServicePreAppointmentDocuments() {
  const ctx = useContext(ServicePreAppointmentDocumentsContext);
  if (!ctx) {
    throw new Error(
      "useServicePreAppointmentDocuments must be used within a ServicePreAppointmentDocumentsProvider",
    );
  }
  return ctx;
}
